import type { AnatomyBinding, AnatomyPartId } from "./componentAnatomy";

/**
 * @purpose One `tokens.text-*` mixin expands to five declarations, four of them
 * token-backed. A label binds a whole text style rather than a font size, so the
 * legend has to say all of it — this is the one place that expansion lives.
 *
 * Source: `styles/tokens/_text-styles.scss`, `text-recipe($size, $weight)`.
 */

type TextScale = "3xs" | "2xs" | "xs" | "s" | "m";

type TextWeight = "400" | "500";

/** Per-scale letter-spacing, which the recipe writes as a literal em value. */
const trackingByScale: Record<TextScale, string> = {
    "3xs": "0",
    "2xs": "0",
    xs: "0",
    s: "-0.005em",
    m: "-0.01em",
};

export const anatomyTextStyle = ({
    scale,
    weight,
    mixin,
    note,
    partId = "label",
}: {
    scale: TextScale;
    weight: TextWeight;
    /** Mixin name, so the legend can point at the line that set the style. */
    mixin: string;
    /** Why this block re-declares the style, when it is an override. */
    note?: string;
    /** Text-bearing part the style lands on; components with several text parts pass theirs. */
    partId?: AnatomyPartId;
}): readonly AnatomyBinding[] => [
    {
        partId,
        property: "font-family",
        token: "--font-family-base",
        note:
            note === undefined
                ? `From @include tokens.${mixin}.`
                : `From @include tokens.${mixin}. ${note}`,
    },
    { partId, property: "font-size", token: `--font-size-${scale}` },
    {
        partId,
        property: "font-weight",
        token: `--font-weight-${weight}`,
    },
    {
        partId,
        property: "line-height",
        token: `--font-height-${scale}`,
    },
    {
        partId,
        property: "letter-spacing",
        literal: trackingByScale[scale],
        note: "Tracking is baked into the text style; Figma has no tracking variable to bind.",
    },
];
